import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SectionStamp } from '../components/Atoms';
import Icon from '../components/Icon';
import DesktopHeader from '../components/DesktopHeader';
import DesktopFooter from '../components/DesktopFooter';
import DesktopWhatsAppFab from '../components/DesktopWhatsAppFab';
import { fonts } from '../theme';

const SERVICES = [
  {
    ic: 'spine', n: '01', title: 'Orthopaedic',
    lead: 'Musculoskeletal injuries, joint pain, post-surgical recovery.',
    details: 'Restoring joint health, reducing pain, and enhancing mobility through personalised exercise programmes, manual therapy and modern equipment.',
    tags: ['Manual therapy', 'Strengthening', 'Sports'],
  },
  {
    ic: 'brain', n: '02', title: 'Neurological',
    lead: 'Helping patients regain strength and coordination.',
    details: 'Evidence-based therapies for stroke, multiple sclerosis and Parkinson’s. Plans focus on motor function, balance and quality of life.',
    tags: ['Stroke', 'MS', 'Parkinson’s'],
  },
  {
    ic: 'baby', n: '03', title: 'Paediatric',
    lead: 'Specialised care for young patients with unique needs.',
    details: 'Therapy for developmental delays, congenital disorders and injuries — engaging programmes tailored to each child’s capacity.',
    tags: ['Developmental', 'Congenital', 'Mobility'],
  },
  {
    ic: 'cane', n: '04', title: 'Geriatric',
    lead: 'Mobility, balance, and overall health for seniors.',
    details: 'Helping older adults stay active, reduce fall risk and improve quality of life through balance and strength training.',
    tags: ['Balance', 'Falls', 'Strength'],
  },
  {
    ic: 'bandage', n: '05', title: 'Post-op rehab',
    lead: 'Joint replacements and other post-surgical recovery.',
    details: 'Accelerate recovery, reduce pain and restore function after surgery — with plans timed to your healing protocol.',
    tags: ['Knee', 'Hip', 'Shoulder'],
  },
];

export default function ServicesDesktop({ t }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(0);

  return (
    <div style={{
      width: '100%', minHeight: '100vh', background: t.paper, color: t.ink,
      fontFamily: fonts.sans, position: 'relative', overflow: 'hidden',
    }}>
      <DesktopHeader t={t} />

      <div style={{ padding: '80px max(56px, 4vw) 40px', maxWidth: 1440, margin: '0 auto' }}>
        <div style={{
          display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 56, alignItems: 'end',
        }}>
          <div>
            <SectionStamp n="—" label="Specialties" t={t}/>
            <h1 style={{
              fontFamily: fonts.serif, fontSize: 'clamp(56px, 7vw, 96px)', fontWeight: 300,
              letterSpacing: '-0.04em', lineHeight: 0.92, margin: '20px 0 0',
              textWrap: 'balance',
            }}>
              What I <span style={{ fontStyle: 'italic', color: t.mintDeep }}>treat.</span>
            </h1>
          </div>
          <p style={{
            fontFamily: fonts.sans, fontSize: 16, color: t.muted, lineHeight: 1.6,
            margin: 0, maxWidth: 480,
          }}>
            Five specialties, a single method: assess hands-on, plan around you, revise every visit. Click any specialty to read how I approach it.
          </p>
        </div>
      </div>

      <div style={{
        padding: '0 max(56px, 4vw) 80px', maxWidth: 1440, margin: '0 auto',
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {SERVICES.map((s, i) => {
            const isOpen = open === i;
            return (
              <div
                key={i}
                role="button"
                tabIndex={0}
                onClick={() => setOpen(isOpen ? -1 : i)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setOpen(isOpen ? -1 : i);
                  }
                }}
                style={{
                  textAlign: 'left', cursor: 'pointer', outline: 'none',
                  background: isOpen ? t.ink : 'transparent',
                  color: isOpen ? t.paper : t.ink,
                  border: `0.5px solid ${isOpen ? t.ink : t.hair}`,
                  borderRadius: 18, padding: '28px 32px',
                  transition: 'background .25s, color .25s',
                  display: 'grid',
                  gridTemplateColumns: '60px 36px 1fr 24px',
                  gridGap: 24, alignItems: 'center',
                }}
              >
                <span style={{
                  fontFamily: fonts.mono, fontSize: 11, letterSpacing: '0.16em',
                  color: isOpen ? t.mint : t.muted,
                }}>{s.n}</span>
                <Icon name={s.ic} size={26} stroke={isOpen ? t.mint : t.ink} sw={1.4}/>
                <div>
                  <div style={{
                    fontFamily: fonts.serif, fontSize: 'clamp(26px, 2.5vw, 36px)', fontWeight: 400,
                    letterSpacing: '-0.02em', lineHeight: 1.05,
                  }}>{s.title}</div>
                  <div style={{
                    fontFamily: fonts.sans, fontSize: 14, lineHeight: 1.5,
                    color: isOpen ? 'rgba(246,243,236,0.75)' : t.muted, marginTop: 6,
                  }}>{s.lead}</div>
                  {isOpen && (
                    <div style={{
                      marginTop: 16, paddingTop: 16,
                      borderTop: `0.5px solid rgba(168,230,200,0.3)`,
                      display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 32,
                      alignItems: 'start',
                    }}>
                      <div style={{
                        fontFamily: fonts.sans, fontSize: 14, lineHeight: 1.6,
                        color: 'rgba(246,243,236,0.85)',
                      }}>{s.details}</div>
                      <div>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16 }}>
                          {s.tags.map(tag => (
                            <span key={tag} style={{
                              fontFamily: fonts.mono, fontSize: 10, letterSpacing: '0.12em',
                              textTransform: 'uppercase', color: t.mint,
                              border: `0.5px solid ${t.mint}66`, borderRadius: 999,
                              padding: '5px 10px',
                            }}>{tag}</span>
                          ))}
                        </div>
                        <button
                          onClick={(e) => { e.stopPropagation(); navigate('/book'); }}
                          style={{
                            appearance: 'none', border: 'none', cursor: 'pointer',
                            background: t.mint, color: t.ink,
                            padding: '12px 18px', borderRadius: 999,
                            fontFamily: fonts.sans, fontSize: 13, fontWeight: 500,
                            display: 'inline-flex', alignItems: 'center', gap: 6,
                          }}
                        >
                          Book this care <Icon name="arrow-up-right" size={14}/>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                <Icon
                  name={isOpen ? 'minus' : 'plus'}
                  size={20}
                  stroke={isOpen ? t.paper : t.ink}
                />
              </div>
            );
          })}
        </div>
      </div>

      <DesktopFooter t={t} />
      <DesktopWhatsAppFab />
    </div>
  );
}
